import React, { FC, useCallback, useMemo } from 'react'
import { localPoint } from '@visx/event'
import { Group } from '@visx/group'
import { withParentSize, WithParentSizeProvidedProps } from '@visx/responsive'
import Pie from '@visx/shape/lib/shapes/Pie'
import { useTooltip } from '@visx/tooltip'
import _sumBy from 'lodash/sumBy'
import { Box, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { TCurrencyPieChartDatum, ITooltipDatum } from '../../types'
import { DataTooltip } from '../DataTooltip'
import { LegendItem } from '../LegendItem'
import {
  NO_DATA,
  getFillColor,
  formatCurrencyWithPercentage,
  formatCompactCurrency,
} from '../utils'
import { CurrencyPieChartArc } from './CurrencyPieChartArc'
import { LegendRowItem } from './LegendRowItem'

export interface ICurrencyPieChartProps {
  data: TCurrencyPieChartDatum[]
  pieThickness?: number
}
interface ResponsiveCurrencyPieChartProps
  extends WithParentSizeProvidedProps,
    ICurrencyPieChartProps {}

const getValue = (dataSet: TCurrencyPieChartDatum) => dataSet.value

const sortData = (data: TCurrencyPieChartDatum[]) =>
  [...data].sort((a: TCurrencyPieChartDatum, b: TCurrencyPieChartDatum) => b.value - a.value)

const CurrencyPieChart: FC<ResponsiveCurrencyPieChartProps> = ({
  data,
  parentWidth: width,
  pieThickness = 50,
}) => {
  const isAllZeros = data.every(({ value }) => value === 0)
  const height = width
  const shouldHaveCountColumn = data.some(set => set.count)
  const radius = Math.min(width, height) / 2

  const sortedData = sortData(data)
  const totalValues = useMemo(() => _sumBy(data, ({ value }) => value), [data])

  const { hideTooltip, showTooltip, tooltipData, tooltipLeft = 0, tooltipTop = 0 } = useTooltip<
    ITooltipDatum
  >()

  const mouseOverHandler = useCallback(
    (dataTooltip: ITooltipDatum) => (e: React.MouseEvent) => {
      const point = localPoint(e)

      if (point === null) {
        return
      }

      const { x, y } = point

      showTooltip({
        tooltipLeft: x,
        tooltipTop: y,
        tooltipData: dataTooltip,
      })
    },
    [showTooltip]
  )

  return (
    <Box display='flex' flexDirection='column' alignItems='center' data-test='donut'>
      <svg width={width} height={height}>
        <Group left={radius} top={radius}>
          <Pie
            data={isAllZeros ? NO_DATA : sortedData}
            pieValue={getValue}
            outerRadius={radius}
            innerRadius={radius - pieThickness}
            cornerRadius={3}
            padAngle={0.005}
            fill='green'>
            {pie => {
              return pie.arcs.map((arc, index) => {
                const { label, value } = arc.data
                const arcPath = pie.path(arc)
                const arcFill = isAllZeros ? brandColors.coolGray1 : getFillColor(index)

                return (
                  arcPath && (
                    <CurrencyPieChartArc
                      key={`${label}-${value}`}
                      arcFill={arcFill}
                      arcPath={arcPath}
                      onMouseLeave={hideTooltip}
                      onMouseOver={mouseOverHandler}
                      datum={{ label, value }}
                      totalValues={totalValues}
                    />
                  )
                )
              })
            }}
          </Pie>
          <text dominantBaseline='middle' textAnchor='middle' fontWeight={600} fontSize={34}>
            {formatCompactCurrency(totalValues)}
          </text>
        </Group>
      </svg>

      {tooltipData && (
        <DataTooltip top={tooltipTop} left={tooltipLeft} title={tooltipData.datum.label}>
          <LegendItem
            text={formatCurrencyWithPercentage(tooltipData.datum.value, tooltipData.totalValues)}
            color={tooltipData.color}
          />
        </DataTooltip>
      )}

      <Box width='100%' display='table' mt={3}>
        <Box display='table-row' mb={1}>
          <Box display='table-cell' />
          <Box display='table-cell' textAlign='right'>
            <Typography variant='subtitle2'>Amount</Typography>
          </Box>
          {shouldHaveCountColumn && (
            <Box display='table-cell' textAlign='right' paddingLeft={2}>
              <Typography variant='subtitle2'>Count</Typography>
            </Box>
          )}
        </Box>

        {sortedData.map(({ count, label, value }, index) => (
          <LegendRowItem
            key={label}
            datum={{ count, label, value }}
            totalValues={totalValues}
            color={getFillColor(index)}
          />
        ))}
      </Box>
    </Box>
  )
}

export default CurrencyPieChart

export const ResponsiveCurrencyPieChart: FC<ICurrencyPieChartProps> = withParentSize(
  CurrencyPieChart
)
